// =============================================================================
// web/src/features/diary/diaryPdf.ts — T-326
//
// Client-side Site Diary PDF report (pdfmake), photos embedded.
// Used by BOTH Josh's internal diary view and the public client portal page.
//
// downloadDiaryPdf(data) fetches each photo (signed URL) -> base64, builds a
// branded pdfmake doc, and triggers a browser download. No upload, no worker.
// =============================================================================

// @ts-ignore — Vite ?url suffix isn't in pdfmake's type declarations
import pdfmakeScriptUrl from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";

// ---- pdfmake loader (mirrors quotePdf.ts) -----------------------------------

function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-pdfmake-tag="${tag}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") resolve();
      else {
        existing.addEventListener("load",  () => resolve());
        existing.addEventListener("error", () => reject(new Error(`Failed to (re)load ${tag}`)));
      }
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.pdfmakeTag = tag;
    script.onload  = () => { script.dataset.loaded = "true"; resolve(); };
    script.onerror = () => reject(new Error(`Failed to load ${tag} from ${src}`));
    document.head.appendChild(script);
  });
}

async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) return existing;
  await loadScriptOnce(pdfmakeScriptUrl,  "pdfmake-core");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  const pdfMake = (window as any).pdfMake;
  if (!pdfMake) throw new Error("window.pdfMake missing after script load");
  return pdfMake;
}

async function fetchAsDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(typeof r.result === "string" ? r.result : null);
      r.onerror   = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ---- brand tokens -----------------------------------------------------------

const COLOR = {
  primary:  "#ff7844",
  ink:      "#111111",
  text:     "#222222",
  muted:    "#666666",
  dim:      "#9a9a9a",
  panel:    "#f6f6f4",
  panelRule:"#ececea",
  headBar:  "#3a3a3a",
  rowAlt:   "#f4f4f3",
};
const COMPANY = { name: "Coretel Group Pty Ltd", web: "coretelgroup.com" };

// ---- data shape -------------------------------------------------------------

export interface DiaryPdfPhoto { url: string; caption?: string | null; kind?: string | null }

export interface DiaryPdfData {
  projectCode:  string;
  projectName:  string;
  siteName?:    string | null;
  clientName?:  string | null;
  entryDate:    string;            // ISO yyyy-mm-dd
  scopeDone?:   string | null;
  delays?:      string | null;
  delayBillable?: boolean;
  delayCalc?:   any | null;
  conditions?:  any | null;
  whoOnSite?:   any | null;
  startedAt?:   string | null;
  endedAt?:     string | null;
  outage?:      any | null;
  photos:       DiaryPdfPhoto[];
}

// ---- formatters -------------------------------------------------------------

function fmtDate(iso: string): string {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-AU", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}
function fmtTime(iso?: string | null): string {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}
function hoursOnSite(start?: string | null, end?: string | null): string {
  if (!start || !end) return "—";
  try {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (ms <= 0) return `${fmtTime(start)} – ${fmtTime(end)}`;
    const h = (ms / 3600000).toFixed(1).replace(/\.0$/, "");
    return `${fmtTime(start)} – ${fmtTime(end)}   ·   ${h}h`;
  } catch { return `${fmtTime(start)} – ${fmtTime(end)}`; }
}
function nameList(arr: any): string {
  if (!Array.isArray(arr) || arr.length === 0) return "—";
  return arr.map((x: any) => (typeof x === "string" ? x : (x?.name ?? x?.full_name ?? ""))).filter(Boolean).join(", ") || "—";
}
function slug(s: string): string {
  return (s || "site").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}
function hasCalc(dc: any): boolean {
  if (!dc) return false;
  const has = (o: any, ...k: string[]) => o && k.some((key) => Number(o[key]) > 0);
  return has(dc.labour, "crew", "hours") || has(dc.plant, "qty", "hours") || has(dc.ta, "qty", "nights");
}

function sectionHeading(text: string) {
  return {
    columns: [
      { text: text.toUpperCase(), color: COLOR.primary, bold: true, fontSize: 9, characterSpacing: 1, width: "auto" },
      { canvas: [{ type: "line", x1: 8, y1: 6, x2: 515, y2: 6, lineWidth: 0.6, lineColor: "#eeeeee" }] },
    ],
    margin: [0, 10, 0, 5],
  };
}

// A label/value pair for the top info card table.
function infoRow(label: string, value: string) {
  return [
    { text: label, color: COLOR.dim, fontSize: 7.5, bold: true, characterSpacing: 0.6 },
    { text: value || "—", color: COLOR.text, fontSize: 9.5 },
  ];
}

// ---- doc builder ------------------------------------------------------------

function buildDocDefinition(d: DiaryPdfData, photoDataUrls: { dataUrl: string; caption?: string | null; kind?: string | null }[]): any {
  const c = d.conditions ?? {};
  const weather = Array.isArray(c.weather) ? c.weather.join(", ") : (c.weather ?? "");
  // T-373 — wind alongside temp; both units when captured.
  const windPart = c.wind_kmh != null
    ? `Wind ${c.wind_kmh} km/h (${c.wind_kt ?? Math.round((c.wind_kmh / 1.852) * 10) / 10} kt)`
    : "";
  const weatherLine = [weather, c.temperature_c != null ? `${c.temperature_c}°C` : "", windPart].filter(Boolean).join("   ·   ") || "—";
  const who = d.whoOnSite ?? {};
  const hasVisitors = Array.isArray(who.visitors) && who.visitors.length > 0;

  const content: any[] = [
    // Header band
    {
      columns: [
        { text: "CORETEL", color: COLOR.primary, bold: true, fontSize: 17, characterSpacing: 1 },
        { text: "SITE DIARY", color: COLOR.dim, fontSize: 9, alignment: "right", characterSpacing: 1.5, margin: [0, 7, 0, 0] },
      ],
    },
    { canvas: [{ type: "line", x1: 0, y1: 3, x2: 515, y2: 3, lineWidth: 2, lineColor: COLOR.primary }], margin: [0, 5, 0, 12] },

    // Title block
    { text: `${d.projectCode}${d.projectName ? "   —   " + d.projectName : ""}`, bold: true, fontSize: 15, color: COLOR.ink, margin: [0, 0, 0, 3] },
    { text: fmtDate(d.entryDate), fontSize: 11, color: COLOR.muted },
    { text: [d.siteName, d.clientName].filter(Boolean).join("    ·    "), fontSize: 8.5, color: COLOR.dim, margin: [0, 1, 0, 12] },

    // Info card — clean label/value table with breathing room
    {
      table: {
        widths: [82, "*"],
        body: [
          infoRow("TIME ON SITE", hoursOnSite(d.startedAt, d.endedAt)),
          // T-373 — wind band colours the conditions value (informational cue only)
          [
            { text: "CONDITIONS", color: COLOR.dim, fontSize: 7.5, bold: true, characterSpacing: 0.6 },
            {
              text: weatherLine,
              color: c.wind_kmh != null && c.wind_kmh >= 46 ? "#dc2626"
                   : c.wind_kmh != null && c.wind_kmh >= 30 ? "#d97706"
                   : COLOR.text,
              fontSize: 9.5,
            },
          ],
          infoRow("CREW", nameList(who.crew)),
          ...(hasVisitors ? [infoRow("VISITORS", nameList(who.visitors))] : []),
        ],
      },
      layout: {
        fillColor: () => COLOR.panel,
        hLineWidth: (i: number) => (i === 0 ? 0 : 0.5),
        hLineColor: () => COLOR.panelRule,
        vLineWidth: () => 0,
        paddingLeft: () => 11, paddingRight: () => 11, paddingTop: () => 6, paddingBottom: () => 6,
      },
      margin: [0, 0, 0, 14],
    },
  ];

  // Work completed
  content.push(sectionHeading("Work completed"));
  content.push({
    text: d.scopeDone?.trim() || "No narrative recorded for this entry.",
    fontSize: 10, color: d.scopeDone ? COLOR.text : COLOR.dim, lineHeight: 1.3, margin: [0, 0, 0, 8],
  });

  // Delays + calc
  const showCalc = hasCalc(d.delayCalc);
  if ((d.delays && d.delays.trim()) || showCalc) {
    content.push(sectionHeading("Delays / blockers"));
    if (d.delayBillable) content.push({ text: "BILLABLE  ·  VARIATION / EOT", color: COLOR.primary, bold: true, fontSize: 7.5, characterSpacing: 0.8, margin: [0, 0, 0, 4] });
    if (d.delays?.trim()) content.push({ text: d.delays.trim(), fontSize: 10, color: COLOR.text, lineHeight: 1.3, margin: [0, 0, 0, 6] });

    if (showCalc) {
      const dc = d.delayCalc ?? {};
      const rows: any[][] = [];
      if (dc.labour && (Number(dc.labour.crew) || Number(dc.labour.hours))) rows.push(["Labour", `${dc.labour.crew || 0} crew × ${dc.labour.hours || 0} hrs`, `${(dc.labour.crew || 0) * (dc.labour.hours || 0)} man-hrs`]);
      if (dc.plant && (Number(dc.plant.qty) || Number(dc.plant.hours)))     rows.push(["Plant", `${dc.plant.qty || 0} × ${dc.plant.hours || 0} hrs`, `${(dc.plant.qty || 0) * (dc.plant.hours || 0)} plant-hrs`]);
      if (dc.ta && (Number(dc.ta.qty) || Number(dc.ta.nights)))             rows.push(["TA", `${dc.ta.qty || 0} × ${dc.ta.nights || 0} nights`, `${(dc.ta.qty || 0) * (dc.ta.nights || 0)} TA nights`]);
      const reasonText = dc.reason ? String(dc.reason).replace(/_/g, " ") : "";
      content.push({
        table: {
          widths: [70, "*", 92],
          body: [
            [{ text: "DELAY IMPACT" + (reasonText ? `   ·   ${reasonText}` : ""), colSpan: 3, color: "#ffffff", bold: true, fontSize: 8, characterSpacing: 0.6 }, {}, {}],
            ...rows.map((r) => [
              { text: r[0], fontSize: 9, color: COLOR.text, bold: true },
              { text: r[1], fontSize: 9, color: COLOR.muted },
              { text: r[2], fontSize: 9, bold: true, color: COLOR.ink, alignment: "right" },
            ]),
          ],
        },
        layout: {
          fillColor: (rowIndex: number) => (rowIndex === 0 ? COLOR.headBar : rowIndex % 2 === 0 ? COLOR.rowAlt : "#ffffff"),
          hLineWidth: () => 0.5, hLineColor: () => "#e8e8e8", vLineWidth: () => 0,
          paddingLeft: () => 9, paddingRight: () => 9, paddingTop: () => 4.5, paddingBottom: () => 4.5,
        },
        margin: [0, 2, 0, 10],
      });
    }
  }

  // Outage
  if (d.outage && (d.outage.carrier || d.outage.ref)) {
    content.push(sectionHeading("Outage"));
    content.push({
      text: [d.outage.carrier, d.outage.type, d.outage.ref].filter(Boolean).join("  ·  ") + (d.outage.notes ? `\n${d.outage.notes}` : ""),
      fontSize: 10, color: COLOR.text, lineHeight: 1.3, margin: [0, 0, 0, 8],
    });
  }

  // Photos — grouped by category (T-329); untagged falls to General. Subheadings
  // only show when more than one category is in use, so simple entries stay clean.
  if (photoDataUrls.length > 0) {
    content.push(sectionHeading(`Photos (${photoDataUrls.length})`));
    const order: { key: string; label: string }[] = [
      { key: "setup",   label: "Site setup" },
      { key: "works",   label: "Works completed" },
      { key: "delay",   label: "Delays / issues" },
      { key: "plant",   label: "Plant" },
      { key: "general", label: "General" },
    ];
    const usedKinds = new Set(photoDataUrls.map((p) => p.kind || "general"));
    const pushGrid = (list: { dataUrl: string; caption?: string | null }[]) => {
      for (let i = 0; i < list.length; i += 2) {
        const pair = [list[i], list[i + 1]].filter(Boolean) as { dataUrl: string; caption?: string | null }[];
        content.push({
          columns: pair.map((p) => ({
            width: "50%",
            stack: [
              { image: p.dataUrl, fit: [243, 185], alignment: "left" },
              p.caption ? { text: p.caption, fontSize: 8, color: COLOR.muted, italics: true, margin: [0, 3, 0, 0] } : { text: "" },
            ],
            margin: [0, 0, 8, 10],
          })),
          columnGap: 8,
        });
      }
    };
    for (const g of order) {
      const list = photoDataUrls.filter((p) => (p.kind || "general") === g.key);
      if (list.length === 0) continue;
      if (usedKinds.size > 1) {
        content.push({ text: `${g.label} (${list.length})`, color: COLOR.headBar, bold: true, fontSize: 8, characterSpacing: 0.5, margin: [0, 5, 0, 3] });
      }
      pushGrid(list);
    }
  }

  return {
    pageSize: "A4",
    pageMargins: [42, 38, 42, 46],
    content,
    defaultStyle: { font: "Roboto", fontSize: 10, color: COLOR.text },
    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        { text: `${COMPANY.name}  ·  ${COMPANY.web}`, color: COLOR.dim, fontSize: 7, margin: [42, 0, 0, 0] },
        { text: `${currentPage} / ${pageCount}`, color: COLOR.dim, fontSize: 7, alignment: "right", margin: [0, 0, 42, 0] },
      ],
    }),
  };
}

// ---- public API -------------------------------------------------------------

export async function downloadDiaryPdf(data: DiaryPdfData): Promise<void> {
  const pdfMake = await getPdfMake();
  const imgs: { dataUrl: string; caption?: string | null; kind?: string | null }[] = [];
  for (const p of data.photos) {
    const dataUrl = await fetchAsDataUrl(p.url);
    if (dataUrl && dataUrl.startsWith("data:image")) imgs.push({ dataUrl, caption: p.caption, kind: p.kind });
  }
  const docDef = buildDocDefinition(data, imgs);
  const fname = `${slug(data.projectCode)}_SiteDiary_${data.entryDate}.pdf`;
  pdfMake.createPdf(docDef).download(fname);
}
